import React, { memo, useRef } from "react";
import {
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Search, CalendarMonth } from "@mui/icons-material";
import dayjs from "dayjs";
import { formatDateOnly } from "../../../../Utils/formatDateTime";

const QueryPanel = ({
  startDate,
  endDate,
  loading,
  activeState,
  showChips,
  isSearchDisabled,
  onStartDateChange,
  onEndDateChange,
  onSearch,
  onClearDates,
}) => {
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        p: "16px",
        overflow: "hidden",
        borderRadius: "12px",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
    >
      <>
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0, 1.55fr) minmax(300px, 0.95fr)",
            },
          }}
        >
          {/* Left: Title + Date Pickers */}
          <Box sx={{ display: "grid", gap: 2, alignItems: "start" }}>
            <Box sx={{ display: "grid", gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--color-text-muted)",
                }}
              >
                Query Window
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  fontSize: { xs: 20, sm: 24, lg: 28 },
                  fontWeight: 700,
                  lineHeight: 1,
                  color: "var(--color-text)",
                }}
              >
                Performance Scan
              </Typography>
              <Typography
                sx={{
                  maxWidth: 560,
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: { xs: 12, sm: 14, lg: 16 },
                  lineHeight: 1.55,
                  color: "var(--color-text-muted)",
                }}
              >
                Define the venue window, then run a comparison sweep across
                model strike and place telemetry.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "minmax(0, 1fr) minmax(0, 1fr)",
                },
              }}
            >
              <div onClick={() => {
                try {
                  startDateRef.current?.showPicker();
                } catch (err) {
                  console.warn("showPicker failed, falling back to click:", err);
                }
              }}
                className="relative flex items-center gap-2.5 p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] cursor-pointer"
              >
                <CalendarMonth className="text-[var(--color-primary)] flex-shrink-0" sx={{ fontSize: 24 }} />
                <div className="flex items-center gap-2 min-w-0">
                  <span className="md:text-sm text-xs label-caps block m-0">FROM:</span>
                  <span className="md:text-sm text-xs label-caps block m-0 !text-[--color-text] !font-bold">
                    {startDate ? formatDateOnly(startDate) : "All"}
                  </span>
                </div>
                <input
                  ref={startDateRef}
                  type="date"
                  value={startDate ? dayjs(startDate).format("YYYY-MM-DD") : ""}
                  onChange={(e) => onStartDateChange(e.target.value || null)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full [color-scheme:dark]"
                />
              </div>

              <div onClick={() => {
                if (startDate) {
                  try {
                    endDateRef.current?.showPicker();
                  } catch (err) {
                    console.warn("showPicker failed, falling back to click:", err);
                  }
                }
              }}
                className={`relative flex items-center gap-2.5 p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] ${startDate ? "cursor-pointer" : "cursor-not-allowed"}`}
              >
                <CalendarMonth className="text-[var(--color-primary)] flex-shrink-0" sx={{ fontSize: 24 }} />
                <div className="flex items-center gap-2 min-w-0">
                  <span className="md:text-sm text-xs label-caps block m-0">TO:</span>
                  <span className={`md:text-sm text-xs label-caps block m-0 ${startDate ? "!text-[--color-text]" : "!text-[var(--color-text-muted)]"} !font-bold`}>
                    {endDate ? formatDateOnly(endDate) : "All"}
                  </span>
                </div>
                <input
                  ref={endDateRef}
                  type="date"
                  value={endDate ? dayjs(endDate).format("YYYY-MM-DD") : ""}
                  min={startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined}
                  disabled={!startDate}
                  onChange={(e) => onEndDateChange(e.target.value || null)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full [color-scheme:dark] disabled:cursor-not-allowed"
                />
              </div>
            </Box>
          </Box>

          {/* Right: Status Badge + Command Layer */}
          <Box sx={{ display: "grid", gap: 2, alignContent: "start" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", lg: "flex-end" },
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  p: "8px 12px",
                  borderRadius: "9999px",
                  background: activeState.background,
                  color: activeState.color,
                  border: activeState.border,
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: activeState.color,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  {activeState.label}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: 2,
                p: "16px",
                borderRadius: "12px",
                background: "var(--color-bg)",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--color-text-muted)",
                }}
              >
                Command Layer
              </Typography>

              <Button
                variant="contained"
                onClick={onSearch}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} sx={{ color: "#10140d" }} />
                  ) : (
                    <Search />
                  )
                }
                disabled={isSearchDisabled}
                sx={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  justifySelf: "start",
                  gap: "8px",
                  width: "100%",
                  minHeight: 42,
                  padding: "10px 12px",
                  border: "1px solid var(--color-primary)",
                  borderRadius: "12px",
                  fontSize: { xs: 12, sm: 14 },
                  background: "var(--color-primary)",
                  fontWeight: 900,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "black",
                  cursor: "pointer",
                  "&.Mui-disabled": {
                    border: "1px solid var(--color-primary)",
                    background: "var(--color-primary)",
                    color: "black",
                    pointerEvents: "auto",
                    cursor: "not-allowed",
                  },
                  "& .MuiButton-startIcon svg": { fontSize: 18 },
                }}
              >
                {loading ? "Scanning" : "Search"}
              </Button>

              {showChips && (
                <Button
                  variant="outlined"
                  onClick={onClearDates}
                  sx={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    justifySelf: "start",
                    gap: "8px",
                    width: "100%",
                    minHeight: 42,
                    padding: "10px 12px",
                    border: "1px solid var(--color-border)",
                    borderRadius: "12px",
                    fontSize: { xs: 12, sm: 14 },
                    background: "var(--color-surface)",
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--color-text-muted)",
                    cursor: "pointer",
                  }}
                >
                  Clear Dates
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Active filter chips */}
        {showChips && (
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              gap: 2,
              mt: 2,
              flexWrap: "wrap",
            }}
          >
            {startDate && !endDate && (
              <Chip
                icon={<CalendarMonth />}
                label={`Date: ${formatDateOnly(startDate)}`}
                onDelete={onClearDates}
                sx={{
                  height: 32,
                  borderRadius: "9999px",
                  background: "var(--color-text-light)",
                  color: "var(--color-text)",
                  border: "1px solid rgba(143, 149, 162, 0.25)",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  "& .MuiChip-icon, & .MuiChip-deleteIcon": {
                    color: "var(--color-text)",
                    fontSize: 18,
                  },
                  "& .MuiChip-deleteIcon:hover": {
                    color: "var(--color-text)",
                  },
                }}
              />
            )}
            {startDate && endDate && (
              <Chip
                icon={<CalendarMonth />}
                label={`Range: ${formatDateOnly(startDate)} - ${formatDateOnly(endDate)}`}
                onDelete={onClearDates}
                sx={{
                  height: 32,
                  borderRadius: "9999px",
                  background: "rgba(255, 168, 0, 0.12)",
                  color: "var(--color-accent-amber)",
                  border: "1px solid rgba(255, 168, 0, 0.25)",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  "& .MuiChip-icon, & .MuiChip-deleteIcon": {
                    color: "var(--color-accent-amber)",
                    fontSize: 18,
                  },
                  "& .MuiChip-deleteIcon:hover": {
                    color: "var(--color-accent-amber)",
                  },
                }}
              />
            )}
          </Box>
        )}
      </>
    </Paper>
  );
};

export default memo(QueryPanel);
